import { preprocess, shortenAudio } from "./utils/audioUtils.js";
import { useState, useRef, useEffect } from "react";
import MicRecorder from "mic-recorder-to-mp3";
import { Howl } from "howler";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faStop,
  faMicrophone,
  faMicrophoneSlash,
  faUpload,
  faMusic,
} from "@fortawesome/free-solid-svg-icons";

const MAX_DURATION_MS = 5 * 60 * 1000; // 5 minutes

function AudioComponent({
  audioData,
  setAudioData,
  setLoading,
  setPredictions,
}) {
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  const mediaRecorderRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [sound, setSound] = useState(null);
  const [audioInfo, setAudioInfo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleAudio = async (e) => {
    setPredictions(null);
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("File size too large. Please select a file smaller than 10MB.");
      return;
    }
    if (file.type !== "audio/mpeg") {
      alert("Only MP3 files are allowed.");
      return;
    }
    setIsUploading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      await decodeAndProcessAudio(arrayBuffer, file.name);
    } catch (error) {
      console.error("Error processing audio file:", error);
      alert("Error processing audio file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const startRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    const recorder = new MicRecorder({ bitRate: 128 });
    mediaRecorderRef.current = recorder;
    recorder
      .start()
      .then(() => {
        setIsRecording(true);
        setTimeout(() => stopRecording(), MAX_DURATION_MS);
      })
      .catch((error) => {
        console.error("Error starting recording:", error);
        alert(
          "Failed to start recording. Please check your microphone permissions."
        );
      });
  };

  const stopRecording = () => {
    setIsRecording(false);
    if (!mediaRecorderRef.current) return;

    mediaRecorderRef.current
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        const reader = new FileReader();
        reader.onload = () =>
          decodeAndProcessAudio(reader.result, "Recorded Audio.mp3");
        reader.readAsArrayBuffer(blob);
      })
      .catch((error) => {
        console.error("Error stopping recording:", error);
        alert("Failed to process the recording. Please try again.");
      });
  };

  const decodeAndProcessAudio = async (arrayBuffer, fileName) => {
    try {
      const blob = new Blob([arrayBuffer], { type: "audio/mpeg" });
      const blobUrl = URL.createObjectURL(blob);
      await audioCtx.resume();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

      if (audioBuffer.duration > MAX_DURATION_MS / 1000) {
        alert(
          "Audio duration exceeds 5 minutes. Please upload a shorter audio file."
        );
        return;
      }

      const newSound = new Howl({
        src: [blobUrl],
        format: ["mp3"],
        onload: () => {
          if (sound) {
            sound.unload();
          }
          setSound(newSound);
          setAudioInfo({
            duration: audioBuffer.duration,
            fileName: fileName,
          });
          URL.revokeObjectURL(blobUrl);
        },
        onplay: () => setIsPlaying(true),
        onpause: () => setIsPlaying(false),
        onstop: () => setIsPlaying(false),
        onend: () => setIsPlaying(false),
        onloaderror: (id, error) => {
          console.error("Failed to load sound:", error);
          URL.revokeObjectURL(blobUrl);
          alert("Failed to load the audio. Please try again.");
        },
      });
      const preprocessedAudio = preprocess(audioBuffer);
      const shortenedAudio = shortenAudio(preprocessedAudio);
      setAudioData(shortenedAudio);
    } catch (error) {
      console.error("Error decoding audio:", error);
    }
  };

  const togglePlayStop = () => {
    if (isPlaying) {
      sound.stop();
    } else {
      sound.play();
    }
  };

  useEffect(() => {
    return () => {
      if (sound) {
        sound.unload();
      }
    };
  }, [sound]);

  return (
    <div className="mx-auto bg-white shadow-lg rounded-lg overflow-hidden h-full flex flex-col mb-1 mt-1 font-sans">
      <div className="p-4 flex-grow overflow-y-auto">
        <h2 className="text-3xl font-extrabold text-center text-gray-800 tracking-wide mb-4 border-b-2 border-gray-200 pb-2">
          Painting Generator via ML
        </h2>
        <div className="flex justify-center space-x-4 mb-6">
          <label
            htmlFor="audioInput"
            className="bg-blue-500 text-white font-semibold py-2 px-4 rounded cursor-pointer hover:bg-blue-600 transition duration-300"
          >
            <FontAwesomeIcon icon={faUpload} className="mr-2" />
            Upload MP3
          </label>
          <input
            onChange={handleAudio}
            type="file"
            id="audioInput"
            accept="audio/mpeg"
            className="hidden"
          />
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`font-semibold py-2 px-4 rounded cursor-pointer transition duration-300 ${
              isRecording
                ? "bg-red-500 hover:bg-red-600"
                : "bg-green-500 hover:bg-green-600"
            } text-white`}
          >
            <FontAwesomeIcon
              icon={isRecording ? faMicrophoneSlash : faMicrophone}
              className="mr-2"
            />
            {isRecording ? "Stop" : "Record"}
          </button>
        </div>

        {isUploading && (
          <div className="text-center text-gray-600 mb-4">
            <p>Uploading and processing audio...</p>
          </div>
        )}

        {audioInfo && (
          <div className="bg-gray-100 rounded-md p-4 mb-2">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-lg mb-2 text-gray-700">
                  <FontAwesomeIcon icon={faMusic} className="mr-2" />
                  Audio Information
                </h3>
                <p className="text-gray-600">File: {audioInfo.fileName}</p>
                <p className="text-gray-600">
                  Duration: {formatDuration(audioInfo.duration)}
                </p>
              </div>
              {sound && (
                <button
                  onClick={togglePlayStop}
                  className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-300"
                >
                  <FontAwesomeIcon
                    icon={isPlaying ? faStop : faPlay}
                    className="mr-2"
                  />
                  {isPlaying ? "Stop" : "Play"}
                </button>
              )}
            </div>
          </div>
        )}

        <button
          className="w-full bg-indigo-500 text-white font-semibold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-indigo-600 transition duration-300 mt-6"
          disabled={!audioData}
          onClick={() => setLoading(true)}
        >
          Analyse Audio
        </button>
      </div>
      <div className="bg-gray-100 p-2 text-center text-sm text-gray-600">
        <span className="text-black font-bold">Note: </span>
        Only MP3 files are supported. Maximum duration: 5 minutes.
      </div>
    </div>
  );
}

export default AudioComponent;
